import * as signalR from "@aspnet/signalr";
import { ssmTokenService } from "./token-service";
import { API } from "../api";

class TrackingService {
    private connection: signalR.HubConnection | null = null;
    private intentionalStop = false;
    private reconnectAttempts = 0;

    constructor() {
        this.connection = null;
    }

    // Utilidad: Genera o recupera un ID único para visitantes anónimos
    getOrCreateSessionId() {
        let sessionId = sessionStorage.getItem("tracking_session_id");
        if (!sessionId) {
            sessionId =
                "anon_" +
                Math.random().toString(36).substr(2, 9) +
                Date.now().toString(36);
            sessionStorage.setItem("tracking_session_id", sessionId);
        }
        return sessionId;
    }

    // Llama a este método al cargar la aplicación globalmente (App.vue o main.js)
    startConnection() {
        this.intentionalStop = false;

        // Si ya hay una conexión activa, no hacer nada
        if (
            this.connection &&
            this.connection.state === signalR.HubConnectionState.Connected
        )
            return;

        const rootPath = (process.env.VUE_APP_ROOT_PATH as string) || "http://localhost:5000/api/";
        const baseUrl = rootPath.replace(/\/api\/$/, "");
        const sessionId = this.getOrCreateSessionId();
        const token = ssmTokenService.getToken();

        // Para evitar problemas de longitud o parseo en la URL con WebSockets,
        // dejamos que SignalR gestione el token internamente.
        // SignalR lo inyectará en la cabecera 'Authorization: Bearer ...'
        // para peticiones HTTP (Negotiate, SSE, LongPolling).
        const hubUrl = `${baseUrl}trackingHub?sessionId=${sessionId}`;

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: () => token || "",
                // Sugerimos transportes HTTP puros si queremos garantizar que el Token
                // viaje siempre por Header y no reviente los límites de la QueryString.
                // En SignalR 1.1.4, si definimos esto se priorizan SSE/LP antes que WS.
                transport: token
                    ? signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
                    : signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling
            })
            .build();

        // En @aspnet/signalr 1.1.4 withAutomaticReconnect no existe, implementamos un onclose básico
        this.connection.onclose((error: any) => {
            if (!this.intentionalStop) {
                console.log("🔄 [Tracking] Conexión cerrada inesperadamente.");
                this.scheduleReconnect();
            }
        });

        // 3. Iniciar la conexión
        this.connection
            .start()
            .then(() => {
                console.log("✅ [Tracking] Conectado al Tracking Hub de SignalR.");
                this.reconnectAttempts = 0; // Resetear intentos de reconexión al conectar
            })
            .catch((err: any) => {
                console.error("❌ [Tracking] Error al conectar SignalR:", err.message || err);
                if (!this.intentionalStop) {
                    this.scheduleReconnect();
                }
            });
    }

    private scheduleReconnect() {
        // Estrategia de Backoff Exponencial (Retardos: 2s, 4s, 8s, 16s, ... hasta 30s)
        const delay = Math.min(2000 * Math.pow(2, this.reconnectAttempts), 30000);
        this.reconnectAttempts++;

        console.log(`⏳ [Tracking] Siguiente intento de conexión en ${delay / 1000}s (Intento #${this.reconnectAttempts})...`);
        setTimeout(() => {
            if (!this.intentionalStop) {
                this.startConnection();
            }
        }, delay);
    }

    stopConnection() {
        this.intentionalStop = true;
        if (this.connection) {
            this.connection
                .stop()
                .then(() =>
                    console.log("🛑 [Tracking] Conexión cerrada intencionalmente.")
                )
                .catch((err: any) => console.error(err));
        }
    }

    // Se recomienda llamar a este metodo al hacer login o logout
    // para que coja el nuevo Token (o lo pierda)
    reconnect() {
        if (this.connection && this.connection.state !== signalR.HubConnectionState.Disconnected) {
            this.intentionalStop = true;
            this.connection.stop().then(() => {
                this.startConnection();
            });
        } else {
            this.startConnection();
        }
    }

    // Rastreo de ruta: Informar al Hub sobre en qué vista se encuentra el usuario
    updateCurrentRoute(path: string) {
        if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
            this.connection
                .invoke("UpdateCurrentRoute", path)
                .catch((err: any) => console.error("Error updating route via SignalR:", err));
        } else {
            // Reintento rápido si el socket apenas se está conectando
            setTimeout(() => {
                if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
                    this.connection
                        .invoke("UpdateCurrentRoute", path)
                        .catch((err: any) => console.error("Error updating route via SignalR (retry):", err));
                }
            }, 1500);
        }
    }
}

export const trackingService = new TrackingService();
